import { z } from 'zod'

/** How a boxing station filters backlog jobs for its assigned SKU. */
export const SKU_ASSIGNMENT_MODE_OPTIONS = ['standard', 'solo', 'multi'] as const
export const SkuAssignmentMode = z.enum(SKU_ASSIGNMENT_MODE_OPTIONS)
export type SkuAssignmentMode = z.infer<typeof SkuAssignmentMode>
export const DEFAULT_SKU_ASSIGNMENT_MODE = SkuAssignmentMode.enum.standard

/**
 * A box sitting in a boxing station's queue, as surfaced to the fulfilment UI
 * team-leader view.
 *
 * `primarySku` is the job's primary SKU — the same column the queue assignment
 * logic works off — and is what the queue chip should display. `skus` is the
 * deduped set of meal weights in the box contents (a multi-SKU box has more
 * than one); it must not be used as the SKU tag, because the first content SKU
 * is not necessarily the primary one.
 */
export const BoxingStationBox = z.object({
  boxId: z.string(),
  serialNumber: z.string(),
  customerName: z.string().nullable(),
  depotCode: z.string(),
  jobId: z.number().int(),
  skus: z.array(z.number()).optional(),
  primarySku: z.number().int().nullable().optional(),
})
export type BoxingStationBox = z.infer<typeof BoxingStationBox>

export const BoxingStation = z.object({
  boxingStationId: z.number().int(),
  stationName: z.string(),
  isOnline: z.boolean(),
  facilityId: z.number().int(),
  primarySku: z.number().int().nullable(),
  secondarySku: z.number().int().nullable(),
  /** Filters backlog jobs for `primarySku` — standard (default), solo-only, or multi-only. */
  primarySkuMode: SkuAssignmentMode.default(DEFAULT_SKU_ASSIGNMENT_MODE),
  /** Filters backlog jobs for `secondarySku` when it is promoted to primary. */
  secondarySkuMode: SkuAssignmentMode.default(DEFAULT_SKU_ASSIGNMENT_MODE),
  boxesInQueue: z.array(BoxingStationBox),
})
export type BoxingStation = z.infer<typeof BoxingStation>

export const BoxingStationSkuInput = z.object({
  primarySku: z.number().int(),
  secondarySku: z.number().int().nullable(),
})
export type BoxingStationSkuInput = z.infer<typeof BoxingStationSkuInput>

export const GetFacilityBoxingStationsQuery = z.object({
  stationName: z.string().optional().describe('Boxing station name'),
})
export type GetFacilityBoxingStationsQuery = z.infer<typeof GetFacilityBoxingStationsQuery>

export const GetFacilityBoxingStationsResponse = z.array(BoxingStation)
export type GetFacilityBoxingStationsResponse = z.infer<typeof GetFacilityBoxingStationsResponse>
